// ReelTimeline — the full-width, TIMECODED, multi-track bottom timeline (a real NLE surface).
// One shared horizontal time axis drives every lane, and EVERY OBJECT ON THE TIMELINE IS
// TAPPABLE — the bottom CONTEXT BAR is a single contextual inspector (the CapCut model:
// simple at rest, tap anything to go deep). Lane stack mirrors the RENDER stack:
//   • Ruler   — timecodes + ticks; click/drag anywhere on empty space = seek.
//   • TRACKS  — every FREE LAYER CLIP on its own row (topmost row = topmost z), on absolute
//     EFFECTIVE reel time: drag the body to move, drag the edges to stretch, with snapping
//     to beat boundaries / other clips / the playhead + a live time readout.
//   • VIDEO   — beats as clips (width ∝ duration): tap = beat context, drag = reorder, right
//     edge = trim; boundary diamonds toggle cut/crossfade (crossfaded cards overlap, as does
//     the time itself).
//   • CC / VO / ♪ — word-timed caption chips, per-beat voiceover segments, the music bed.
// The toolbar holds ONLY the transport (play/pause + timecode) + the stale hint + Render.
// Edits NEVER auto-render — the reel is rebuilt only on explicit Render.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Copy,
  Image as ImageIcon,
  ImagePlus,
  Loader2,
  Mic,
  Minus,
  Music,
  Music2,
  PanelLeft,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Sparkles,
  Square,
  Trash2,
  Type,
  Upload,
} from 'lucide-react'
import { cn } from 'lib/utils'
import { useFileUpload } from 'modules/common/hooks/use-file-upload'
import { useVideoUpload } from 'modules/common/hooks/use-video-upload'
import type {
  Layer,
  TextAnimPreset,
  TextLayer,
  VideoBeatComposition,
  VideoLayerClip,
} from '../types'
import { MIN_CLIP_SEC } from '../video-time'
import { PlayheadLine, ReelTimecode } from './PlayheadWidgets'
import { useAttachmentUrl } from '../use-attachment-url'
import { BeatBackgroundFill } from './VideoTimeline'
import { BG_LABEL, ROLE_LABEL, directSrcUrl } from './video-bg'
import {
  useComposerEditor,
  useVideoTimeline,
  type VideoTimelineContextValue,
} from './CompositionEditorProvider'
import { MIN_BEAT_SECONDS, MAX_BEAT_SECONDS } from './video-ops'
import { GLOSSY_PILL } from './toolbar-style'

// Lane geometry — the gutter rows mirror these exactly so labels line up with the tracks.
const RULER_H = 20
const VIDEO_LANE_H = 72
// THICK, readable track rows (the old 20px stripes were unreadable).
const TRACK_ROW_H = 32
// Clips whose windows meet at a boundary (or graze by a few frames) still SHARE a row —
// only a real time-overlap opens a new one.
const CLIP_PACK_EPS = 0.05
// Track rows shown before the stack stops growing (protects the dock height; extra clips
// stay editable on the canvas + surface via the overflow hint).
const MAX_TRACK_ROWS = 5
const CAP_LANE_H = 22
// Tall enough for BOTH zones of a recorded segment: waveform strip + the script line.
const VO_LANE_H = 44
const MUSIC_LANE_H = 24
const LANE_GAP = 2
const GUTTER_W = 48
const MIN_PX_PER_SEC = 46
const TRIM_HANDLE_W = 9
const TRIM_STEP = 0.5

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

/** m:ss timecode (e.g. 0:03, 1:07). */
export function fmtTC(sec: number): string {
  const s = Math.max(0, Math.round(sec))
  const m = Math.floor(s / 60)
  return `${m}:${(s % 60).toString().padStart(2, '0')}`
}

type Geom = { spans: Array<{ start: number; end: number }>; total: number; pxPerSec: number }
type Gesture =
  | { kind: 'seek' }
  | { kind: 'trim'; index: number }
  | { kind: 'reorder'; from: number; startX: number; moved: boolean }
  // Free layer clips: drag the body (move, grabOffset = where inside the clip you grabbed)
  // or one edge (stretch). Both snap; both show a live time readout while dragging.
  | {
      kind: 'clipMove'
      layerId: string
      grabOffset: number
      len: number
      startX: number
      moved: boolean
    }
  | { kind: 'clipResize'; layerId: string; edge: 'start' | 'end' }
type Drag = { from: number; dx: number; target: number }
/** Which timeline OBJECT is selected — drives what the context bar shows. */
type LaneFocus = 'clip' | 'vo' | 'cc' | 'music' | 'layer'

/** The full-width bottom timeline. Renders null when the active post isn't a video composition. */
export function ReelTimelineDock({ onAskAIBeat }: { onAskAIBeat?: (index: number) => void }) {
  const timeline = useVideoTimeline()
  if (!timeline) return null
  return <ReelTimelineInner timeline={timeline} onAskAIBeat={onAskAIBeat} />
}

function ReelTimelineInner({
  timeline,
  onAskAIBeat,
}: {
  timeline: VideoTimelineContextValue
  onAskAIBeat?: (index: number) => void
}) {
  const { beats, activeIndex } = timeline
  const [laneFocus, setLaneFocus] = useState<LaneFocus>('clip')
  // The left inspector column (selected object's controls). CLOSED by default — the tap-time
  // QUICK MENU is the first tier (like the Layers dock pattern); "More options" or the toolbar
  // toggle opens this depth tier.
  const [inspectorOpen, setInspectorOpen] = useState(false)
  // The inline QUICK MENU: appears over the tapped timeline object with its most-expected
  // actions + Ask Taka + More options. Coordinates are relative to the dock root.
  const [quickMenu, setQuickMenu] = useState<{
    focus: LaneFocus
    layerId: string | null
    // The beat the menu was opened OVER, by id — actions must not chase activeIndex, which
    // playback/pause moves under an open menu (Space is not a dismissal).
    beatId: string | null
    left: number
    top: number
    below: boolean
  } | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const quickMenuRef = useRef<HTMLDivElement | null>(null)
  // The element under the CURRENT press — captured at pointerdown so gesture-resolved taps
  // (beat cards, track clips) can anchor the menu to the item, not the release point.
  const tapAnchorRef = useRef<Element | null>(null)
  const openQuickMenu = useCallback(
    (
      focus: LaneFocus,
      el: Element | null,
      layerId: string | null = null,
      beatId: string | null = null
    ) => {
      const root = rootRef.current
      if (!root || !el) return
      const r = el.getBoundingClientRect()
      const rr = root.getBoundingClientRect()
      // Items on the topmost lane would push the menu over the toolbar — flip below them.
      const below = r.top - rr.top < 90
      setQuickMenu({
        focus,
        layerId,
        beatId,
        left: clamp(r.left - rr.left + r.width / 2, 110, Math.max(110, rr.width - 110)),
        top: below ? r.bottom - rr.top + 6 : r.top - rr.top - 6,
        below,
      })
    },
    []
  )
  const openQuickMenuRef = useRef(openQuickMenu)
  openQuickMenuRef.current = openQuickMenu
  // Dismiss: press anywhere outside the menu (capture phase — item handlers stopPropagation),
  // Escape, or scrolling the lanes (the anchor coordinates go stale).
  useEffect(() => {
    if (!quickMenu) return
    const onDown = (e: PointerEvent) => {
      if (quickMenuRef.current?.contains(e.target as Node)) return
      setQuickMenu(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setQuickMenu(null)
    }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [quickMenu])
  // Playback under an open menu moves the playhead/active beat — close it (Space isn't a
  // pointerdown, so the outside-press dismissal never sees it).
  useEffect(() => {
    if (timeline.playing) setQuickMenu(null)
  }, [timeline.playing])
  const focusRef = useRef(setLaneFocus)
  focusRef.current = setLaneFocus
  // The selected FREE LAYER CLIP (timeline-side identity; the canvas editor mirrors it).
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null)
  // The ACTIVE-BEAT VIEW editor — layer content edits + canvas selection go through it
  // (undo + autosave for free). Null outside the composition editor (then the dock isn't mounted).
  const composer = useComposerEditor()
  const editorRef = useRef(composer?.editor ?? null)
  editorRef.current = composer?.editor ?? null

  // Geometry: EFFECTIVE beat spans (crossfades overlap) + a px/sec that fills the width (or
  // scrolls when the reel is long). Timeline time = master time, everywhere.
  const spans = timeline.spans
  const total = timeline.totalDuration

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const innerRef = useRef<HTMLDivElement | null>(null)
  const [areaW, setAreaW] = useState(0)
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const measure = () => setAreaW(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const safeArea = areaW > 0 ? areaW : 640
  const innerW = Math.max(safeArea, total * MIN_PX_PER_SEC)
  const pxPerSec = total > 0 ? innerW / total : MIN_PX_PER_SEC

  // Live geometry for the pointer handlers (avoids stale closures during a gesture).
  const geomRef = useRef<Geom>({ spans, total, pxPerSec })
  geomRef.current = { spans, total, pxPerSec }
  const timelineRef = useRef(timeline)
  timelineRef.current = timeline

  const [drag, setDrag] = useState<Drag | null>(null)
  // The "Add a beat" intent menu (generate a scene / animate an image / upload your video).
  const [addOpen, setAddOpen] = useState(false)
  // Live window readout while a clip gesture runs (shown on the dragged chip).
  const [clipDrag, setClipDrag] = useState<{ layerId: string; start: number; end: number } | null>(
    null
  )
  const gesture = useRef<Gesture | null>(null)

  const beatAtTime = useCallback((t: number): number => {
    const g = geomRef.current
    for (let i = g.spans.length - 1; i >= 0; i--) if (t >= g.spans[i]!.start - 1e-6) return i
    return 0
  }, [])

  const clientXToTime = useCallback((clientX: number): number => {
    const el = innerRef.current
    const g = geomRef.current
    if (!el || g.pxPerSec <= 0) return 0
    const rect = el.getBoundingClientRect()
    return clamp((clientX - rect.left) / g.pxPerSec, 0, g.total)
  }, [])
  // Unclamped-right variant for the TRIM gesture: the last beat's end IS the reel end, so a
  // total-clamped time could only ever shorten it — extending needs room past `total` (the
  // beat's own MIN/MAX clamp in trimBeat bounds the result).
  const clientXToTimeFree = useCallback((clientX: number): number => {
    const el = innerRef.current
    const g = geomRef.current
    if (!el || g.pxPerSec <= 0) return 0
    const rect = el.getBoundingClientRect()
    return Math.max(0, (clientX - rect.left) / g.pxPerSec)
  }, [])

  // Magnetic snapping: beat boundaries, other clips' edges, the playhead, 0 and the reel end.
  // ~8px of pull at the current zoom; `exclude` keeps a dragged clip from snapping to itself.
  const snapTime = useCallback((t: number, exclude?: string): number => {
    const g = geomRef.current
    const tl = timelineRef.current
    const cands: number[] = [0, g.total, tl.getCurrentTime()]
    for (const s of g.spans) cands.push(s.start, s.end)
    for (const c of tl.clips) {
      if (c.layer.id === exclude) continue
      cands.push(c.start, c.end)
    }
    const thresh = 8 / g.pxPerSec
    let best = t
    let bestD = thresh
    for (const c of cands) {
      const d = Math.abs(c - t)
      if (d < bestD) {
        bestD = d
        best = c
      }
    }
    return best
  }, [])

  // Select a timeline object: move the playhead/stage to its beat + focus its context.
  const selectObject = useCallback(
    (beatIndex: number | null, focus: LaneFocus) => {
      if (beatIndex != null) timeline.seek(geomRef.current.spans[beatIndex]?.start ?? 0)
      setLaneFocus(focus)
      if (focus !== 'layer') setSelectedClipId(null)
    },
    [timeline]
  )

  // Select a FREE CLIP: land the stage on the beat its window starts in, mirror the selection
  // into the canvas editor (bi-directional), and open the layer context.
  const selectClip = useCallback(
    (layerId: string) => {
      const tl = timelineRef.current
      const clip = tl.clips.find(c => c.layer.id === layerId)
      if (!clip) return
      // Land the playhead ON the clip's first visible moment (not just its beat's start) —
      // the paused canvas is playhead-true, so this is what makes the selected clip show.
      timeline.seek(Math.min(clip.start, geomRef.current.total - 0.01))
      setSelectedClipId(layerId)
      setLaneFocus('layer')
      // After seek → selectBeat → view resync, land the canvas selection on the clip's layer
      // (React batches these in order, so the resync's select(null) loses to this select).
      editorRef.current?.select(layerId)
    },
    [timeline]
  )

  // ── Pointer gestures (seek / trim / reorder) driven off one document-level move+up pair ──
  // teardown removes the active gesture's document listeners (used on unmount mid-gesture too).
  const teardown = useRef<() => void>(() => {})
  const beginGesture = useCallback(
    (g: Gesture, e: React.PointerEvent) => {
      const onMove = (ev: PointerEvent) => {
        const cur = gesture.current
        if (!cur) return
        const t = clientXToTime(ev.clientX)
        if (cur.kind === 'seek') {
          timeline.seek(t)
        } else if (cur.kind === 'trim') {
          timeline.trimBeat(
            cur.index,
            clientXToTimeFree(ev.clientX) - (geomRef.current.spans[cur.index]?.start ?? 0)
          )
        } else if (cur.kind === 'clipMove') {
          // A ≤4px press is a TAP (opens the quick menu on release), not a micro-drag.
          if (!cur.moved && Math.abs(ev.clientX - cur.startX) > 4) cur.moved = true
          if (!cur.moved) return
          // Slide the whole clip: keep the grab point under the cursor, snap the LEADING edge
          // first (fall back to the trailing edge), keep length.
          const raw = t - cur.grabOffset
          let start = snapTime(raw, cur.layerId)
          if (start === raw) start = snapTime(raw + cur.len, cur.layerId) - cur.len
          timeline.moveClip(cur.layerId, start)
          const g = geomRef.current
          const s = clamp(start, 0, g.total - cur.len)
          setClipDrag({ layerId: cur.layerId, start: s, end: s + cur.len })
        } else if (cur.kind === 'clipResize') {
          const snapped = snapTime(t, cur.layerId)
          timeline.resizeClip(cur.layerId, cur.edge, snapped)
          const c = timelineRef.current.clips.find(x => x.layer.id === cur.layerId)
          if (c) {
            setClipDrag({
              layerId: cur.layerId,
              start: cur.edge === 'start' ? Math.min(snapped, c.end - MIN_CLIP_SEC) : c.start,
              end: cur.edge === 'end' ? Math.max(snapped, c.start + MIN_CLIP_SEC) : c.end,
            })
          }
        } else if (cur.kind === 'reorder') {
          if (!cur.moved && Math.abs(ev.clientX - cur.startX) > 4) cur.moved = true
          if (cur.moved)
            setDrag({ from: cur.from, dx: ev.clientX - cur.startX, target: beatAtTime(t) })
        }
      }
      const cleanup = () => {
        document.removeEventListener('pointermove', onMove)
        document.removeEventListener('pointerup', onUp)
        document.removeEventListener('pointercancel', onCancel)
        // Close the history bracket on EVERY gesture end (release, cancel, unmount teardown):
        // trim/clipMove/clipResize commit per pointer-move, and the bracket coalesces the whole
        // drag into one undo step. No-op for kinds that never opened one (seek/reorder).
        timeline.endHistoryGesture()
      }
      // A cancelled pointer (touch scroll takeover, window blur mid-drag) ends the gesture
      // like a release WITHOUT committing a reorder drop.
      const onCancel = () => {
        cleanup()
        gesture.current = null
        setDrag(null)
        setClipDrag(null)
      }
      const onUp = (ev: PointerEvent) => {
        const cur = gesture.current
        cleanup()
        gesture.current = null
        if (cur?.kind === 'clipMove' || cur?.kind === 'clipResize') setClipDrag(null)
        if (cur?.kind === 'clipMove' && !cur.moved) {
          // Clean tap on a track clip → its quick menu (selection already happened on grab).
          openQuickMenuRef.current('layer', tapAnchorRef.current, cur.layerId)
        }
        if (cur?.kind === 'trim') focusRef.current('clip')
        if (cur?.kind === 'reorder') {
          if (cur.moved) {
            // Compute the drop target from the release position directly (not from the drag state,
            // which a fast release could leave a frame stale).
            const target = beatAtTime(clientXToTime(ev.clientX))
            if (target !== cur.from) timeline.moveBeat(cur.from, target)
          } else {
            timeline.seek(geomRef.current.spans[cur.from]?.start ?? 0)
            // Clean tap on a beat card → its quick menu (target pinned by beat id).
            openQuickMenuRef.current(
              'clip',
              tapAnchorRef.current,
              null,
              timelineRef.current.beats[cur.from]?.id ?? null
            )
          }
          focusRef.current('clip')
          setDrag(null)
        }
      }
      // Arm: tear down any PREVIOUS gesture's listeners first (a new pointerdown while an old
      // gesture's pointerup was lost — e.g. released outside the window — must not leave two
      // live move handlers double-processing every event). Order matters: the old teardown
      // nulls gesture.current, so arm the new gesture AFTER it runs.
      teardown.current()
      gesture.current = g
      teardown.current = () => {
        cleanup()
        gesture.current = null
      }
      // Kinds that commit per pointer-move → open a history bracket so the drag is one undo step.
      if (g.kind === 'trim' || g.kind === 'clipMove' || g.kind === 'clipResize') {
        timeline.beginHistoryGesture()
      }
      document.addEventListener('pointermove', onMove)
      document.addEventListener('pointerup', onUp)
      document.addEventListener('pointercancel', onCancel)
      e.preventDefault()
    },
    [beatAtTime, clientXToTime, clientXToTimeFree, snapTime, timeline]
  )
  useEffect(() => () => teardown.current(), [])

  // Keyboard. Space toggles play/pause — but NEVER steal keys from a focused control: typing
  // fields, contentEditable, real buttons, and role=button elements. With a clip selected:
  // ←/→ nudge its window ±0.1s (Shift = ±1s), Delete removes it, Cmd/Ctrl+D duplicates it.
  // Reads live state through refs so the listener isn't re-attached on every playhead tick
  // (the timeline context identity changes at rAF rate during playback).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tl = timelineRef.current
      const el = document.activeElement as HTMLElement | null
      const tag = el?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return
      const isSpace = e.code === 'Space' || e.key === ' '
      if (isSpace) {
        if (tag === 'BUTTON' || el?.getAttribute('role') === 'button') return
        e.preventDefault()
        if (tl.playing) tl.pause()
        else tl.play()
        return
      }
      const clipId = laneFocus === 'layer' ? selectedClipId : null
      if (!clipId) return
      const clip = tl.clips.find(c => c.layer.id === clipId)
      if (!clip) return
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault()
        const d = (e.key === 'ArrowLeft' ? -1 : 1) * (e.shiftKey ? 1 : 0.1)
        // moveClip preserves length at the reel edges (independent-edge clamping would
        // silently shrink the clip against 0 / the reel end).
        tl.moveClip(clipId, clip.start + d)
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault()
        tl.removeClip(clipId)
        setSelectedClipId(null)
      } else if ((e.metaKey || e.ctrlKey) && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault()
        tl.duplicateClip(clipId)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [laneFocus, selectedClipId])

  // Canvas → timeline selection sync: tapping a layer ON THE CANVAS highlights its clip and
  // opens the layer context here (the timeline → canvas direction lives in selectClip).
  const canvasSelectedId = composer?.editor.selectedId ?? null
  // Also re-runs on timeline.clips: a JUST-ADDED layer is selected on the canvas BEFORE its
  // clip materializes in the write-back reconcile — without the clips dep the mirror never
  // fires for it, selectedClipId stays null, and the layer context dies after one op (ops
  // resync the view, which nulls the canvas selection fallback).
  useEffect(() => {
    if (!canvasSelectedId) return
    if (!timeline.clips.some(c => c.layer.id === canvasSelectedId)) return
    setSelectedClipId(canvasSelectedId)
    setLaneFocus('layer')
  }, [canvasSelectedId, timeline.clips])

  // Ruler ticks: a readable step for the reel's length.
  const step = total <= 8 ? 1 : total <= 20 ? 2 : total <= 45 ? 5 : 10
  const ticks: number[] = []
  for (let s = 0; s <= total + 1e-6; s += step) ticks.push(s)

  // TRACKS: free clips PACK into shared rows — a clip opens a NEW row only when it overlaps
  // in time with every existing row (any-editor behavior: five sequential titles = one row).
  // Packing walks top-z first, so clips that DO overlap stack top row = top z (mirrors the
  // render order). Row assignments FREEZE while a clip drag/resize is live — live re-packing
  // would hop the chip between rows under the pointer; the release re-packs.
  const packed = useMemo(() => {
    const ordered = [...timeline.clips].sort((a, b) => b.layer.z - a.layer.z || a.start - b.start)
    const rows: Array<Array<{ start: number; end: number }>> = []
    const rowOf = new Map<string, number>()
    for (const c of ordered) {
      let r = rows.findIndex(row =>
        row.every(o => c.start >= o.end - CLIP_PACK_EPS || c.end <= o.start + CLIP_PACK_EPS)
      )
      if (r === -1) {
        rows.push([])
        r = rows.length - 1
      }
      rows[r]!.push({ start: c.start, end: c.end })
      rowOf.set(c.layer.id, r)
    }
    return { ordered, rowOf, rowCount: rows.length }
  }, [timeline.clips])
  const frozenPack = useRef(packed)
  if (clipDrag == null) frozenPack.current = packed
  const pack = clipDrag == null ? packed : frozenPack.current
  const rowFor = (id: string) => pack.rowOf.get(id) ?? packed.rowOf.get(id) ?? 0
  // Capped to keep the dock height sane; clips packed beyond the cap surface via the hint.
  const trackClips = packed.ordered.filter(c => rowFor(c.layer.id) < MAX_TRACK_ROWS)
  const trackOverflow = packed.ordered.length - trackClips.length
  const trackRowCount = Math.max(1, Math.min(pack.rowCount, MAX_TRACK_ROWS))
  const tracksLaneH = trackRowCount * TRACK_ROW_H + (trackRowCount - 1) * 2
  const laneStackH =
    tracksLaneH + VIDEO_LANE_H + CAP_LANE_H + VO_LANE_H + MUSIC_LANE_H + LANE_GAP * 5

  return (
    <div ref={rootRef} className="relative flex shrink-0 flex-col border-t border-line bg-bg-2">
      <TimelineToolbar
        timeline={timeline}
        total={total}
        inspectorOpen={inspectorOpen}
        onToggleInspector={() => setInspectorOpen(o => !o)}
      />
      {addOpen && <AddBeatMenu timeline={timeline} onClose={() => setAddOpen(false)} />}

      <div className="flex items-start">
        {/* The contextual INSPECTOR: a left column beside the lanes, same width as the Design
            dock above it. Height is LOCKED to the lane stack — long contexts scroll inside,
            they never stretch the timeline. */}
        {inspectorOpen && (
          <div
            className="w-[300px] shrink-0 overflow-y-auto border-r border-line bg-bg-2 px-4 py-3"
            style={{ height: RULER_H + laneStackH }}
          >
            <ContextBar
              timeline={timeline}
              focus={laneFocus}
              onAskAIBeat={onAskAIBeat}
              selectedClipId={selectedClipId}
              onClipGone={() => setSelectedClipId(null)}
            />
          </div>
        )}

        {/* Fixed left gutter: lane labels aligned to each track row (clicking one focuses it). */}
        <div
          className="flex shrink-0 flex-col border-r border-line/60 bg-bg-2"
          style={{ width: GUTTER_W }}
        >
          <div style={{ height: RULER_H }} />
          <LaneLabel
            h={tracksLaneH}
            active={laneFocus === 'layer'}
            onClick={() => setLaneFocus('layer')}
          >
            Layers
          </LaneLabel>
          <LaneLabel
            h={VIDEO_LANE_H}
            active={laneFocus === 'clip'}
            onClick={() => setLaneFocus('clip')}
          >
            Video
          </LaneLabel>
          <LaneLabel h={CAP_LANE_H} active={laneFocus === 'cc'} onClick={() => setLaneFocus('cc')}>
            CC
          </LaneLabel>
          <LaneLabel h={VO_LANE_H} active={laneFocus === 'vo'} onClick={() => setLaneFocus('vo')}>
            <Mic size={10} className="mr-0.5" /> VO
          </LaneLabel>
          <LaneLabel
            h={MUSIC_LANE_H}
            active={laneFocus === 'music'}
            onClick={() => setLaneFocus('music')}
          >
            <Music size={11} />
          </LaneLabel>
        </div>

        {/* Scrollable time area — the shared x-axis for ruler + all lanes + the playhead. */}
        <div
          ref={scrollRef}
          className="relative flex-1 overflow-x-auto overflow-y-hidden"
          onScroll={() => setQuickMenu(null)}
        >
          <div
            ref={innerRef}
            className="relative select-none"
            style={{ width: innerW, height: RULER_H + laneStackH }}
            onPointerDown={e => beginGesture({ kind: 'seek' }, e)}
          >
            {/* Ruler */}
            <div className="relative border-b border-line/50" style={{ height: RULER_H }}>
              {ticks.map(s => (
                <div
                  key={s}
                  className="absolute top-0 flex h-full flex-col"
                  style={{ left: s * pxPerSec }}
                >
                  <span
                    className="pl-1 text-[9px] leading-none text-paper-faint tabular-nums"
                    style={{ marginTop: 3 }}
                  >
                    {fmtTC(s)}
                  </span>
                </div>
              ))}
            </div>

            {/* TRACKS — free layer clips, one thick row each, topmost row = topmost z. Sits
                ABOVE the video lane because layers render above the video. */}
            <div className="relative" style={{ height: tracksLaneH, marginTop: LANE_GAP }}>
              {trackClips.length === 0 && (
                <div className="absolute inset-y-1 left-2 flex items-center rounded-lg border border-dashed border-line/60 px-3 text-[10px] text-paper-faint">
                  Add text or images on the canvas — they land here as clips you can drag in time.
                </div>
              )}
              {trackClips.map(clip => (
                <TrackClipChip
                  key={clip.layer.id}
                  clip={clip}
                  row={rowFor(clip.layer.id)}
                  pxPerSec={pxPerSec}
                  selected={
                    laneFocus === 'layer' &&
                    (selectedClipId ?? composer?.editor.selectedId) === clip.layer.id
                  }
                  dragReadout={clipDrag?.layerId === clip.layer.id ? clipDrag : null}
                  onBodyDown={e => {
                    // Grabbing a clip selects it (stage lands on its beat, canvas mirrors it),
                    // then the same press drags it in time. A clean tap opens the quick menu.
                    tapAnchorRef.current = e.currentTarget
                    selectClip(clip.layer.id)
                    beginGesture(
                      {
                        kind: 'clipMove',
                        layerId: clip.layer.id,
                        grabOffset: clientXToTime(e.clientX) - clip.start,
                        len: clip.end - clip.start,
                        startX: e.clientX,
                        moved: false,
                      },
                      e
                    )
                  }}
                  onEdgeDown={(edge, e) => {
                    setSelectedClipId(clip.layer.id)
                    setLaneFocus('layer')
                    beginGesture({ kind: 'clipResize', layerId: clip.layer.id, edge }, e)
                  }}
                />
              ))}
              {trackOverflow > 0 && (
                <div
                  className="absolute right-1 bottom-0 rounded bg-black/60 px-1.5 py-0.5 text-[9px] text-paper-faint"
                  title={`${trackOverflow} more layer${trackOverflow > 1 ? 's' : ''}, select them on the canvas`}
                >
                  +{trackOverflow} more
                </div>
              )}
            </div>

            {/* VIDEO lane — beats on their EFFECTIVE spans (crossfaded cards overlap, like the
                time itself). Boundary DIAMONDS toggle how each beat enters (cut ↔ crossfade). */}
            <div className="relative" style={{ height: VIDEO_LANE_H, marginTop: LANE_GAP }}>
              {beats.map((beat, i) => {
                const x = (spans[i]?.start ?? 0) * pxPerSec
                const w = beat.durationSec * pxPerSec
                const isDragged = drag?.from === i
                const isTarget = drag != null && drag.target === i && drag.from !== i
                return (
                  <ClipCard
                    key={beat.id}
                    beat={beat}
                    index={i}
                    x={x}
                    w={w}
                    height={VIDEO_LANE_H}
                    active={i === activeIndex && laneFocus === 'clip'}
                    dragged={isDragged}
                    target={isTarget}
                    generating={timeline.generatingBeatIds.has(beat.id)}
                    hydrating={timeline.hydration != null}
                    dragDx={isDragged ? (drag?.dx ?? 0) : 0}
                    onSelect={() => selectObject(i, 'clip')}
                    onNudge={dir => timeline.moveBeat(i, clamp(i + dir, 0, beats.length - 1))}
                    onBodyPointerDown={e => {
                      tapAnchorRef.current = e.currentTarget
                      beginGesture({ kind: 'reorder', from: i, startX: e.clientX, moved: false }, e)
                    }}
                    onTrimPointerDown={e => beginGesture({ kind: 'trim', index: i }, e)}
                  />
                )
              })}
              {timeline.canAdd && (
                <button
                  type="button"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={() => setAddOpen(o => !o)}
                  title="Add a beat: generate a scene, animate an image, or upload your video"
                  aria-expanded={addOpen}
                  className="absolute top-0 flex flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-line-bright text-paper-dim transition-colors hover:border-acid/60 hover:bg-bg-3 hover:text-paper"
                  style={{ left: total * pxPerSec + 6, height: VIDEO_LANE_H, width: 56 }}
                >
                  <Plus size={16} />
                  <span className="text-[9px] font-medium">Add</span>
                </button>
              )}
              {/* Transition DIAMONDS at every boundary: tap toggles cut ↔ crossfade. Sits at
                  the incoming beat's start (mid-overlap when dissolving). */}
              {beats.map((beat, i) => {
                if (i === 0) return null
                const xfade = (beat.transitionIn ?? 'cut') === 'crossfade'
                const cx =
                  (spans[i]?.start ?? 0) * pxPerSec +
                  (xfade ? ((spans[i - 1]?.end ?? 0) - (spans[i]?.start ?? 0)) * pxPerSec * 0.5 : 0)
                return (
                  <button
                    key={`tr-${beat.id}`}
                    type="button"
                    onPointerDown={e => e.stopPropagation()}
                    onClick={() => timeline.setBeatTransition(i, xfade ? 'cut' : 'crossfade')}
                    title={
                      xfade
                        ? 'Crossfade (~0.4s dissolve), tap for a hard cut'
                        : 'Hard cut, tap to dissolve (~0.4s)'
                    }
                    aria-label={`Transition into beat ${i + 1}: ${xfade ? 'crossfade' : 'cut'}`}
                    className={cn(
                      'absolute top-1/2 z-20 flex h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 items-center justify-center rounded-[3px] border transition-colors',
                      xfade
                        ? 'border-acid bg-acid text-bg'
                        : 'border-line-bright bg-bg text-paper-faint hover:border-acid/70 hover:text-paper'
                    )}
                    style={{ left: cx }}
                  >
                    <span className="-rotate-45 text-[8px] leading-none font-bold">
                      {xfade ? 'X' : '|'}
                    </span>
                  </button>
                )
              })}
            </div>

            {/* CAPTIONS lane — chips are tappable (captions context: placement, styles later). */}
            <div className="relative" style={{ height: CAP_LANE_H, marginTop: LANE_GAP }}>
              {beats.map((beat, i) => (
                <CaptionsRow
                  key={beat.id}
                  beat={beat}
                  start={spans[i]?.start ?? 0}
                  pxPerSec={pxPerSec}
                  active={laneFocus === 'cc'}
                  onTap={el => {
                    selectObject(i, 'cc')
                    openQuickMenu('cc', el, null, beat.id)
                  }}
                />
              ))}
            </div>

            {/* VO lane — per-beat voiceover segments showing the script; tap = edit the script. */}
            <div className="relative" style={{ height: VO_LANE_H, marginTop: LANE_GAP }}>
              {beats.map((beat, i) => (
                <VoSegment
                  key={beat.id}
                  beat={beat}
                  index={i}
                  start={spans[i]?.start ?? 0}
                  pxPerSec={pxPerSec}
                  active={laneFocus === 'vo' && i === activeIndex}
                  voicing={timeline.voicingBeatIds.has(beat.id)}
                  onTap={el => {
                    selectObject(i, 'vo')
                    openQuickMenu('vo', el, null, beat.id)
                  }}
                />
              ))}
            </div>

            {/* MUSIC lane — the bed as ONE tappable object (its context owns on/off + track).
                Lifecycle: dashed off → pulsing while the bed composes → WAVEFORM once locked. */}
            <div className="relative" style={{ height: MUSIC_LANE_H, marginTop: LANE_GAP }}>
              <MusicBar
                timeline={timeline}
                width={Math.max(0, total * pxPerSec)}
                focused={laneFocus === 'music'}
                onTap={el => {
                  selectObject(null, 'music')
                  openQuickMenu('music', el)
                }}
              />
            </div>

            {/* Playhead — spans ruler + all lanes. A per-tick SUBSCRIBER so playback
                repaints only this line, never the whole timeline. */}
            <PlayheadLine timeline={timeline} pxPerSec={pxPerSec} />
          </div>
        </div>
      </div>

      {/* The tap-time QUICK MENU: floats over the tapped object with its most-expected
          actions + Ask Taka + More options (which opens the inspector depth tier). */}
      {quickMenu && (
        <div
          ref={quickMenuRef}
          className={cn('absolute z-40 -translate-x-1/2', !quickMenu.below && '-translate-y-full')}
          style={{ left: quickMenu.left, top: quickMenu.top }}
        >
          <TimelineQuickActions
            key={`${quickMenu.focus}:${quickMenu.layerId ?? quickMenu.beatId ?? timeline.activeIndex}`}
            focus={quickMenu.focus}
            layerId={quickMenu.layerId}
            beatId={quickMenu.beatId}
            timeline={timeline}
            onAskTaka={
              onAskAIBeat
                ? () => {
                    const pinned = quickMenu.beatId
                      ? timeline.beats.findIndex(b => b.id === quickMenu.beatId)
                      : -1
                    onAskAIBeat(pinned >= 0 ? pinned : timeline.activeIndex)
                    setQuickMenu(null)
                  }
                : undefined
            }
            onMore={() => {
              setInspectorOpen(true)
              setQuickMenu(null)
            }}
            onClose={() => setQuickMenu(null)}
            onClipGone={() => setSelectedClipId(null)}
          />
        </div>
      )}
    </div>
  )
}

/** The quick menu's content: 1-2 most-expected actions per object type, then Ask Taka, then
 *  More options. Destructive actions use the same two-tap arm as the inspector. */
function TimelineQuickActions({
  focus,
  layerId,
  beatId,
  timeline,
  onAskTaka,
  onMore,
  onClose,
  onClipGone,
}: {
  focus: LaneFocus
  layerId: string | null
  beatId: string | null
  timeline: VideoTimelineContextValue
  onAskTaka?: () => void
  onMore: () => void
  onClose: () => void
  onClipGone: () => void
}) {
  const [armed, setArmed] = useState(false)
  // Same 3s auto-disarm as the inspector + canvas delete flows — an armed Delete must not
  // sit live indefinitely while the menu stays open.
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (armTimer.current) clearTimeout(armTimer.current)
    },
    []
  )
  const arm = () => {
    setArmed(true)
    if (armTimer.current) clearTimeout(armTimer.current)
    armTimer.current = setTimeout(() => setArmed(false), 3000)
  }
  // Resolve the target by the beat id captured at menu-open — activeIndex can move under an
  // open menu (playback/pause re-selects the playhead beat) and Delete/Duplicate must hit
  // the tapped beat, never the one the playhead happens to sit in.
  const pinnedIndex = beatId ? timeline.beats.findIndex(b => b.id === beatId) : -1
  const index = beatId ? pinnedIndex : timeline.activeIndex
  const beat = index >= 0 ? timeline.beats[index] : undefined
  const voicing = beat ? timeline.voicingBeatIds.has(beat.id) : false
  const canRecord =
    !!beat && timeline.voEnabled && !!beat.vo.trim() && (!beat.voSrc || beat.voDirty)
  return (
    <div className={cn('flex items-center gap-0.5 p-1', GLOSSY_PILL)}>
      {focus === 'clip' && beat && (
        <>
          {timeline.canAdd && (
            <QuickBtn
              label="Duplicate beat"
              icon={<Copy size={13} />}
              onClick={() => {
                timeline.duplicateBeat(index)
                onClose()
              }}
            />
          )}
          {timeline.canDelete && (
            <QuickBtn
              label={armed ? 'Tap again to delete' : 'Delete beat'}
              icon={<Trash2 size={13} />}
              danger={armed}
              onClick={() => {
                if (!armed) return arm()
                timeline.deleteBeat(index)
                onClose()
              }}
            >
              {armed ? 'Confirm' : null}
            </QuickBtn>
          )}
        </>
      )}
      {focus === 'vo' && beat && canRecord && (
        <QuickBtn
          label="Record this beat's voice for the live preview"
          icon={voicing ? <Loader2 size={12} className="animate-spin" /> : <Mic size={12} />}
          onClick={() => {
            if (!voicing) timeline.voiceBeat(beat.id)
            onClose()
          }}
        >
          {voicing ? 'Recording…' : 'Record voice'}
        </QuickBtn>
      )}
      {focus === 'cc' && (
        <QuickBtn
          label={timeline.captionsEnabled ? 'Turn captions off' : 'Turn captions on'}
          icon={<Type size={12} />}
          onClick={() => {
            timeline.setCaptionsEnabled(!timeline.captionsEnabled)
            onClose()
          }}
        >
          {timeline.captionsEnabled ? 'Captions off' : 'Captions on'}
        </QuickBtn>
      )}
      {focus === 'music' && (
        <QuickBtn
          label={timeline.musicEnabled ? 'Turn the music bed off' : 'Turn the music bed on'}
          icon={timeline.musicEnabled ? <Music2 size={12} /> : <Music size={12} />}
          onClick={() => {
            timeline.toggleMusic()
            onClose()
          }}
        >
          {timeline.musicEnabled ? 'Music off' : 'Music on'}
        </QuickBtn>
      )}
      {focus === 'layer' && layerId && (
        <>
          <QuickBtn
            label="Duplicate this clip (Cmd+D)"
            icon={<Copy size={13} />}
            onClick={() => {
              timeline.duplicateClip(layerId)
              onClose()
            }}
          />
          <QuickBtn
            label="Delete this clip (Delete)"
            icon={<Trash2 size={13} />}
            onClick={() => {
              timeline.removeClip(layerId)
              onClipGone()
              onClose()
            }}
          />
        </>
      )}
      {onAskTaka && (
        // The ONE canonical Ask-Taka look shared by every inline menu: stars icon + the words.
        <QuickBtn label="Ask Taka about this" icon={<Sparkles size={13} />} onClick={onAskTaka}>
          Ask Taka
        </QuickBtn>
      )}
      <span className="mx-0.5 h-4 w-px bg-line" aria-hidden />
      <QuickBtn
        label="More options (open the inspector)"
        icon={<PanelLeft size={13} />}
        onClick={onMore}
      />
    </div>
  )
}

function QuickBtn({
  label,
  icon,
  danger,
  onClick,
  children,
}: {
  label: string
  icon: ReactNode
  danger?: boolean
  onClick: () => void
  /** Optional words — icon-only (tooltip carries the label) unless the words carry STATE. */
  children?: ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'flex h-7 min-w-7 items-center justify-center gap-1.5 rounded-lg px-1.5 text-[11px] font-medium whitespace-nowrap transition-colors',
        danger ? 'bg-red-500 text-white' : 'text-paper-dim hover:bg-bg-3 hover:text-paper'
      )}
    >
      {icon}
      {children}
    </button>
  )
}

function LaneLabel({
  h,
  active,
  onClick,
  children,
}: {
  h: number
  active?: boolean
  onClick?: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center justify-center text-[9px] font-semibold tracking-wide uppercase transition-colors',
        active ? 'text-acid' : 'text-paper-faint hover:text-paper-dim'
      )}
      style={{ height: h, marginTop: LANE_GAP }}
    >
      {children}
    </button>
  )
}

function ClipCard({
  beat,
  index,
  x,
  w,
  height,
  active,
  dragged,
  target,
  generating,
  hydrating,
  dragDx,
  onSelect,
  onNudge,
  onBodyPointerDown,
  onTrimPointerDown,
}: {
  beat: VideoBeatComposition
  index: number
  x: number
  w: number
  height: number
  active: boolean
  dragged: boolean
  target: boolean
  generating: boolean
  hydrating: boolean
  dragDx: number
  onSelect: () => void
  onNudge: (dir: number) => void
  onBodyPointerDown: (e: React.PointerEvent) => void
  onTrimPointerDown: (e: React.PointerEvent) => void
}) {
  // Lifecycle: a blueprint-PLANNED scene (no clip yet) reads as a dashed ghost; while the
  // hydrator films it, the shared generating veil pulses on top; a landed clip is just content.
  const planned =
    beat.background.kind === 'video' && beat.background.planned === true && !beat.background.clipSrc
  return (
    <div
      className={cn(
        'absolute top-0 overflow-hidden rounded-lg border outline-none focus-visible:ring-2 focus-visible:ring-acid',
        // The ACTIVE card stacks above the transition diamonds (z-20): its trim handle hugs
        // the right edge, and a diamond sitting on that cut swallows the trim pointerdown
        // otherwise (the card is a stacking context, so the handle's own z can never beat a
        // sibling's). Same fix as the mobile timeline (found live on prod: trim was dead on
        // every crossfade-followed beat); the occluded diamond stays reachable by selecting
        // the neighbor beat.
        dragged ? 'z-30 opacity-90 shadow-2xl' : active ? 'z-[21]' : 'z-0',
        planned && 'border-dashed',
        active
          ? 'border-acid ring-2 ring-acid/40'
          : target
            ? 'border-acid/70'
            : 'border-line hover:border-paper-faint',
        planned && !active && 'border-line-bright'
      )}
      style={{
        left: x,
        width: Math.max(20, w),
        height,
        transform: dragged ? `translateX(${dragDx}px)` : undefined,
      }}
      onPointerDown={e => {
        // Don't let a clip press bubble to the lane's seek handler (that would clobber the reorder
        // gesture with a seek). Trim handle + Add button stop propagation on their own.
        e.stopPropagation()
        // beginGesture preventDefaults the pointerdown, which suppresses the browser's
        // click-focus — focus explicitly so Cmd+Z/keys work right after grabbing a beat
        // (otherwise focus stays wherever it was, e.g. the chat box).
        e.currentTarget.focus()
        onBodyPointerDown(e)
      }}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault()
          onNudge(-1)
        } else if (e.key === 'ArrowRight') {
          e.preventDefault()
          onNudge(1)
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Beat ${index + 1} (${ROLE_LABEL[beat.role]})`}
      aria-current={active}
    >
      <ClipFilmstrip beat={beat} />
      <span className="absolute top-1 left-1 rounded bg-black/60 px-1 text-[9px] leading-tight font-semibold text-paper tabular-nums">
        {index + 1}
      </span>
      <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[8px] leading-tight font-semibold tracking-wide text-paper-dim uppercase">
        {ROLE_LABEL[beat.role]}
      </span>
      <span className="absolute right-1 bottom-1 rounded bg-black/60 px-1 text-[8px] leading-tight font-medium text-paper-dim tabular-nums">
        {beat.durationSec.toFixed(1)}s
      </span>
      {generating && <GeneratingVeil label="Generating…" />}
      {!generating && planned && hydrating && <GeneratingVeil label="Filming…" />}
      {/* Right-edge trim handle */}
      <div
        onPointerDown={e => {
          e.stopPropagation()
          // Focus the beat card (the handle itself isn't focusable): the gesture's
          // preventDefault suppresses click-focus, and Cmd+Z right after a trim would
          // otherwise land in the chat box instead of the reel undo.
          ;(e.currentTarget.closest('[role="button"]') as HTMLElement | null)?.focus()
          onTrimPointerDown(e)
        }}
        title="Drag to trim"
        className="absolute top-0 right-0 bottom-0 cursor-col-resize hover:bg-acid/40"
        style={{ width: TRIM_HANDLE_W }}
      >
        <div className="absolute top-1/2 right-1 h-5 w-0.5 -translate-y-1/2 rounded bg-paper-faint/70" />
      </div>
    </div>
  )
}

function CaptionsRow({
  beat,
  start,
  pxPerSec,
  active,
  onTap,
}: {
  beat: VideoBeatComposition
  start: number
  pxPerSec: number
  active: boolean
  onTap: (el: HTMLElement) => void
}) {
  const caps = beat.captions ?? []
  const chipCls = cn(
    'absolute top-0.5 flex h-[calc(100%-4px)] items-center overflow-hidden rounded px-1 text-[8px] transition-colors',
    active ? 'bg-bg-3 text-paper' : 'bg-bg-3 text-paper-dim hover:text-paper'
  )
  const stop = (e: React.PointerEvent) => e.stopPropagation()
  if (caps.length === 0) {
    // No word timings yet (they're derived on render) — show the VO span as a hint.
    if (!beat.vo.trim()) return null
    return (
      <button
        type="button"
        onPointerDown={stop}
        onClick={e => onTap(e.currentTarget)}
        className="absolute top-0.5 flex h-[calc(100%-4px)] items-center overflow-hidden rounded border border-dashed border-line/60 px-1.5 text-[8px] text-paper-faint hover:text-paper-dim"
        style={{ left: start * pxPerSec + 2, width: Math.max(20, beat.durationSec * pxPerSec - 4) }}
        title="Captions are word-timed from the voiceover on Render · tap for caption settings"
      >
        <span className="truncate">{beat.vo}</span>
      </button>
    )
  }
  return (
    <>
      {caps.map((c, i) => (
        <button
          key={i}
          type="button"
          onPointerDown={stop}
          onClick={e => onTap(e.currentTarget)}
          className={chipCls}
          style={{
            left: (start + c.start) * pxPerSec + 1,
            width: Math.max(12, (c.end - c.start) * pxPerSec - 2),
          }}
          title={`${c.text} · tap for caption settings`}
        >
          <span className="truncate">{c.text}</span>
        </button>
      ))}
    </>
  )
}

/** The visible text of a text layer (for the segment label). */
function textOf(layer: TextLayer): string {
  return layer.runs
    .map(r => r.text)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** A short human label for any layer (segment text). */
export function layerLabel(l: Layer): string {
  if (l.type === 'text') return textOf(l) || 'Text'
  if (l.name) return l.name
  if (l.type === 'logo') return 'Logo'
  if (l.type === 'image') return 'Image'
  if (l.type === 'shape') return 'Shape'
  return 'Overlay'
}

/** ONE free layer clip on its own track row: drag the body to slide it in time, drag the
 *  edges to stretch it, tap to select (canvas mirrors). Thick enough to read: icon + label +
 *  a live `a – b s` readout while selected or dragging. Hidden layers show ghosted. */
function TrackClipChip({
  clip,
  row,
  pxPerSec,
  selected,
  dragReadout,
  onBodyDown,
  onEdgeDown,
}: {
  clip: VideoLayerClip
  row: number
  pxPerSec: number
  selected: boolean
  dragReadout: { start: number; end: number } | null
  onBodyDown: (e: React.PointerEvent) => void
  onEdgeDown: (edge: 'start' | 'end', e: React.PointerEvent) => void
}) {
  const l = clip.layer
  const win = dragReadout ?? clip
  const left = win.start * pxPerSec
  const width = Math.max(26, (win.end - win.start) * pxPerSec)
  const label = layerLabel(l)
  const Icon =
    l.type === 'text' ? Type : l.type === 'shape' || l.type === 'overlay' ? Square : ImageIcon
  const dragging = dragReadout != null
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Layer clip "${label.slice(0, 30)}", ${win.start.toFixed(1)} to ${win.end.toFixed(1)} seconds`}
      onPointerDown={e => {
        e.stopPropagation()
        // beginGesture preventDefaults the pointerdown, which suppresses the browser's
        // click-focus — focus explicitly so the arrows/Delete/Cmd+D keyboard works right
        // after grabbing a clip (otherwise focus stays wherever it was, e.g. the chat box).
        e.currentTarget.focus()
        onBodyDown(e)
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault()
          onBodyDown(e as unknown as React.PointerEvent)
        }
      }}
      className={cn(
        'absolute flex cursor-grab items-center gap-1.5 overflow-hidden rounded-md border px-2 transition-colors outline-none active:cursor-grabbing',
        selected || dragging
          ? 'z-10 border-acid bg-bg-3 text-paper ring-1 ring-acid/40'
          : 'border-line-bright bg-bg-3/80 text-paper-dim hover:border-paper-faint hover:text-paper',
        l.hidden && 'opacity-40'
      )}
      style={{ left, width, top: row * (TRACK_ROW_H + 2), height: TRACK_ROW_H }}
      title={`${label} · ${win.start.toFixed(1)}–${win.end.toFixed(1)}s${l.anim?.in ? ` · in:${l.anim.in}` : ''}${l.anim?.out ? ` · out:${l.anim.out}` : ''}, drag to move, edges to stretch`}
    >
      <Icon size={11} className="shrink-0 opacity-70" />
      <span className="truncate text-[10px] font-medium">{label}</span>
      {(selected || dragging) && (
        <span className="ml-auto shrink-0 pr-1 text-[9px] text-paper-faint tabular-nums">
          {win.start.toFixed(1)}–{win.end.toFixed(1)}s
        </span>
      )}
      <div
        onPointerDown={e => {
          e.stopPropagation()
          onEdgeDown('start', e)
        }}
        title="Drag: when this clip appears"
        className="absolute top-0 bottom-0 left-0 w-2 cursor-ew-resize hover:bg-acid/60"
      >
        <div className="absolute top-1/2 left-[3px] h-3.5 w-0.5 -translate-y-1/2 rounded bg-paper-faint/60" />
      </div>
      <div
        onPointerDown={e => {
          e.stopPropagation()
          onEdgeDown('end', e)
        }}
        title="Drag: when this clip leaves"
        className="absolute top-0 right-0 bottom-0 w-2 cursor-ew-resize hover:bg-acid/60"
      >
        <div className="absolute top-1/2 right-[3px] h-3.5 w-0.5 -translate-y-1/2 rounded bg-paper-faint/60" />
      </div>
    </div>
  )
}

// ── Waveforms + the unified generation lifecycle ─────────────────────────────────────────────
// One visual language for every generated asset on the timeline:
//   WAITING    → dashed ghost chip naming what will exist ("Add voiceover", planned scene);
//   GENERATING → the chip pulses with a spinner ("Recording…", "Filming…", "Composing…");
//   READY      → the real content: a WAVEFORM for audio, the frame/thumbnail for video.
// Audio peaks decode via WebAudio and cache per url; when fetching/decoding is blocked (CORS
// on a signed url), a deterministic url-seeded pseudo-wave keeps the lane reading as audio.

const waveCache = new Map<string, number[] | 'pending'>()
// EVERY mounted subscriber of a pending url registers a waiter — completion notifies all of
// them (not just the instance that started the fetch), so a remounted segment never sits on
// the ghost state waiting for an unrelated re-render.
const waveWaiters = new Map<string, Set<() => void>>()

function pseudoPeaks(url: string, buckets: number): number[] {
  let h = 2166136261
  for (let i = 0; i < url.length; i++) h = ((h ^ url.charCodeAt(i)) * 16777619) >>> 0
  return Array.from({ length: buckets }, () => {
    h = (h * 1103515245 + 12345) >>> 0
    return 0.2 + ((h % 1000) / 1000) * 0.7
  })
}

function useWaveformPeaks(url: string | null | undefined, buckets: number): number[] | null {
  const [, force] = useState(0)
  useEffect(() => {
    if (!url || Array.isArray(waveCache.get(url))) return
    const waiters = waveWaiters.get(url) ?? new Set<() => void>()
    waveWaiters.set(url, waiters)
    const notify = () => force(v => v + 1)
    waiters.add(notify)
    if (!waveCache.has(url)) {
      waveCache.set(url, 'pending')
      void (async () => {
        try {
          const res = await fetch(url)
          if (!res.ok) throw new Error(`http-${res.status}`)
          const buf = await res.arrayBuffer()
          const Ctx =
            window.AudioContext ??
            (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
          if (!Ctx) throw new Error('no-audio-context')
          const ctx = new Ctx()
          const audio = await ctx.decodeAudioData(buf)
          void ctx.close()
          const data = audio.getChannelData(0)
          const n = 256 // decode at a fixed resolution; render resamples to any width
          const step = Math.max(1, Math.floor(data.length / n))
          const peaks: number[] = []
          for (let i = 0; i < n; i++) {
            let max = 0
            const s = i * step
            for (let j = s; j < Math.min(s + step, data.length); j += 32)
              max = Math.max(max, Math.abs(data[j]!))
            peaks.push(max)
          }
          const top = Math.max(0.01, ...peaks)
          waveCache.set(
            url,
            peaks.map(p => p / top)
          )
        } catch {
          waveCache.set(url, pseudoPeaks(url, 256))
        }
        waveWaiters.get(url)?.forEach(fn => fn())
        waveWaiters.delete(url)
      })()
    }
    return () => {
      waiters.delete(notify)
    }
  }, [url])
  const v = url ? waveCache.get(url) : null
  if (!Array.isArray(v)) return null
  // Resample the cached 256-bucket wave down to the requested width.
  const out: number[] = []
  for (let i = 0; i < buckets; i++) out.push(v[Math.floor((i / buckets) * v.length)] ?? 0)
  return out
}

function WaveformBars({ peaks, className }: { peaks: number[]; className?: string }) {
  return (
    <div
      className={cn('pointer-events-none flex h-full items-center gap-[1px]', className)}
      aria-hidden
    >
      {peaks.map((p, i) => (
        <div
          key={i}
          className="w-[2px] shrink-0 rounded-full bg-acid/70"
          style={{ height: `${Math.max(10, p * 90)}%` }}
        />
      ))}
    </div>
  )
}

/** The music bed bar: off (dashed) → composing (pulse, while hydration runs with no locked
 *  src) → locked bed (waveform). One tappable object; its context owns on/off. */
function MusicBar({
  timeline,
  width,
  focused,
  onTap,
}: {
  timeline: VideoTimelineContextValue
  width: number
  focused: boolean
  onTap: (el: HTMLElement) => void
}) {
  const src = timeline.musicSrc
  const attId = src?.kind === 'attachment' ? src.attachmentId : undefined
  const resolved = useAttachmentUrl(attId)
  const url = timeline.musicEnabled ? (directSrcUrl(src ?? undefined) ?? resolved) : undefined
  const peaks = useWaveformPeaks(url, Math.max(24, Math.floor(width / 4)))
  const composing = timeline.musicEnabled && !src && timeline.hydration != null
  return (
    <button
      type="button"
      onPointerDown={e => e.stopPropagation()}
      onClick={e => onTap(e.currentTarget)}
      aria-label={timeline.musicEnabled ? 'Music bed (on)' : 'Music bed (off)'}
      className={cn(
        'absolute top-0 h-full overflow-hidden rounded border px-2 text-[9px] font-medium transition-colors',
        focused ? 'ring-2 ring-acid/40' : '',
        timeline.musicEnabled
          ? 'border-acid/40 bg-acid/10 text-paper-dim hover:bg-acid/15'
          : 'border-dashed border-line/60 text-paper-faint hover:bg-bg-3'
      )}
      style={{ left: 0, width }}
    >
      {peaks ? (
        <>
          <WaveformBars peaks={peaks} className="absolute inset-x-2 inset-y-0.5 opacity-80" />
          <span className="pointer-events-none absolute bottom-0.5 left-1 flex items-center gap-1 rounded bg-black/60 px-1 text-[8px] leading-tight text-paper-dim">
            <Music size={8} /> Music bed
          </span>
        </>
      ) : (
        <span className="flex h-full items-center gap-1">
          {timeline.musicEnabled ? <Music size={10} /> : <Music2 size={10} />}
          {timeline.musicEnabled
            ? composing
              ? 'Music bed'
              : 'Music bed · composes with the reel'
            : 'Music off'}
        </span>
      )}
      {composing && <GeneratingVeil label="Composing…" />}
    </button>
  )
}

// ── Filmstrip (real frames off the beat's clip) ─────────────────────────────────────────────
// Extracts a handful of REAL frames from the clip mp4 (seek + canvas capture) so a beat card
// reads like an NLE filmstrip instead of one stretched poster. Falls back silently to the
// single-frame BeatBackgroundFill when capture is blocked (CORS taint) or the src isn't a
// playable url. Cached per url so scrubbing/re-renders never re-decode.
const filmCache = new Map<string, string[] | 'pending' | 'failed'>()
const filmWaiters = new Map<string, Set<() => void>>()
const FILM_FRAMES = 5

function useClipFrames(url: string | undefined): string[] | null {
  const [, force] = useState(0)
  useEffect(() => {
    if (!url) return
    const cached = filmCache.get(url)
    if (Array.isArray(cached) || cached === 'failed') return
    // Register as a waiter FIRST (even when another instance started the extraction), so an
    // unmount/remount mid-extraction still gets notified — and the extraction itself always
    // runs to completion + caches, never leaving a stuck 'pending'.
    const waiters = filmWaiters.get(url) ?? new Set<() => void>()
    filmWaiters.set(url, waiters)
    const notify = () => force(v => v + 1)
    waiters.add(notify)
    if (!filmCache.has(url)) {
      filmCache.set(url, 'pending')
      const video = document.createElement('video')
      video.crossOrigin = 'anonymous'
      video.muted = true
      video.preload = 'auto'
      video.src = url
      const seekTo = (t: number) =>
        new Promise<void>((resolve, reject) => {
          const onSeek = () => {
            video.removeEventListener('seeked', onSeek)
            resolve()
          }
          video.addEventListener('seeked', onSeek)
          video.addEventListener('error', () => reject(new Error('video-error')), { once: true })
          video.currentTime = t
        })
      void (async () => {
        try {
          await new Promise<void>((resolve, reject) => {
            video.addEventListener('loadedmetadata', () => resolve(), { once: true })
            video.addEventListener('error', () => reject(new Error('video-error')), { once: true })
          })
          const dur = Number.isFinite(video.duration) ? video.duration : 0
          if (dur <= 0) throw new Error('no-duration')
          const canvas = document.createElement('canvas')
          canvas.width = 68
          canvas.height = 120
          const ctx = canvas.getContext('2d')
          if (!ctx) throw new Error('no-ctx')
          const frames: string[] = []
          for (let k = 0; k < FILM_FRAMES; k++) {
            await seekTo(((k + 0.5) / FILM_FRAMES) * dur)
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
            frames.push(canvas.toDataURL('image/jpeg', 0.55)) // throws on a tainted canvas → fallback
          }
          filmCache.set(url, frames)
        } catch {
          filmCache.set(url, 'failed')
        } finally {
          video.removeAttribute('src')
          video.load()
          filmWaiters.get(url)?.forEach(fn => fn())
          filmWaiters.delete(url)
        }
      })()
    }
    return () => {
      waiters.delete(notify)
    }
  }, [url])
  const v = url ? filmCache.get(url) : null
  return Array.isArray(v) ? v : null
}

/** The filmstrip strip behind a beat card (real frames), or null to use the poster fill. */
export function ClipFilmstrip({ beat }: { beat: VideoBeatComposition }) {
  const bg = beat.background
  const clipSrc = bg.kind === 'video' ? (bg.clipSrc ?? bg.candidates?.[0]) : undefined
  const attId = clipSrc?.kind === 'attachment' ? clipSrc.attachmentId : undefined
  const resolved = useAttachmentUrl(attId)
  const url = directSrcUrl(clipSrc) ?? resolved
  const frames = useClipFrames(url)
  if (!frames) return <BeatBackgroundFill bg={bg} />
  return (
    <div className="absolute inset-0 flex bg-black" aria-hidden>
      {frames.map((f, i) => (
        <img
          key={i}
          src={f}
          alt=""
          draggable={false}
          className="h-full min-w-0 flex-1 object-cover"
        />
      ))}
    </div>
  )
}

/** The pulsing GENERATING veil (shared lifecycle look for clips/VO/music). */
export function GeneratingVeil({ label }: { label?: string }) {
  return (
    <div className="absolute inset-0 flex animate-pulse items-center justify-center gap-1.5 bg-bg/60 backdrop-blur-[1px]">
      <Loader2 size={12} className="animate-spin text-acid" />
      {label && <span className="text-[9px] font-medium text-paper">{label}</span>}
    </div>
  )
}

/** A beat's voiceover segment: dashed ghost → pulsing "Recording…" → WAVEFORM + script. */
function VoSegment({
  beat,
  index,
  start,
  pxPerSec,
  active,
  voicing,
  onTap,
}: {
  beat: VideoBeatComposition
  index: number
  start: number
  pxPerSec: number
  active: boolean
  voicing: boolean
  onTap: (el: HTMLElement) => void
}) {
  const hasVo = !!beat.vo.trim()
  const attId = beat.voSrc?.kind === 'attachment' ? beat.voSrc.attachmentId : undefined
  const resolved = useAttachmentUrl(attId)
  const recorded = !!beat.voSrc && !beat.voDirty
  const url = recorded ? (directSrcUrl(beat.voSrc ?? undefined) ?? resolved) : undefined
  const width = Math.max(24, beat.durationSec * pxPerSec - 4)
  const peaks = useWaveformPeaks(url, Math.max(12, Math.floor(width / 4)))
  return (
    <button
      type="button"
      onPointerDown={e => e.stopPropagation()}
      onClick={e => onTap(e.currentTarget)}
      aria-label={`Voiceover for beat ${index + 1}`}
      title={hasVo ? `“${beat.vo}” · tap to edit` : 'Add a voiceover for this beat'}
      className={cn(
        'absolute top-0.5 h-[calc(100%-4px)] overflow-hidden rounded border px-1.5 transition-colors',
        active
          ? 'border-acid bg-bg-3 text-paper ring-1 ring-acid/40'
          : recorded || hasVo
            ? 'border-line bg-bg-3 text-paper-dim hover:text-paper'
            : 'border-dashed border-line/60 text-paper-faint hover:text-paper-dim'
      )}
      style={{ left: start * pxPerSec + 2, width }}
    >
      {peaks ? (
        // Recorded: waveform in the TOP zone, script on its own bottom line — two zones,
        // no overlap, so both stay readable.
        <>
          <WaveformBars peaks={peaks} className="absolute inset-x-1.5 top-1 h-[20px]" />
          <span className="pointer-events-none absolute inset-x-1.5 bottom-1 truncate text-left text-[9px] leading-tight text-paper-dim">
            “{beat.vo}”
          </span>
        </>
      ) : (
        <span className="flex h-full items-center gap-1 text-[9px]">
          <Mic size={9} className="shrink-0" />
          <span className="truncate italic">{hasVo ? `“${beat.vo}”` : 'Add voiceover'}</span>
        </span>
      )}
      {voicing && <GeneratingVeil label="Recording…" />}
    </button>
  )
}

/** The "Add a beat" INTENT menu: a new beat starts as what you mean it to be — a generated
 *  scene (prompt → t2v), an animated image (upload → i2v), or your own footage (mp4, locked
 *  as the clip). Media uploads run BEFORE the beat is added, so a cancelled picker never
 *  leaves a stray duplicate beat on the timeline. */
function AddBeatMenu({
  timeline,
  onClose,
}: {
  timeline: VideoTimelineContextValue
  onClose: () => void
}) {
  const [prompt, setPrompt] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const {
    handleUpload: uploadVideo,
    isUploading: uploadingVideo,
    validationError,
    resetValidationError,
  } = useVideoUpload({ fieldId: 'video', uploadMethod: 'button' })
  const imageUpload = useFileUpload({ allowedTypes: ['image/'], maxSize: 15 * 1024 * 1024 })
  const busy = uploadingVideo || imageUpload.isUploading

  const generate = () => {
    if (!prompt.trim()) return
    const id = timeline.addBeat()
    if (id)
      void timeline.regenerateBeatBackground(id, { shotKind: 't2v', sceneIdea: prompt.trim() })
    onClose()
  }
  const onImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setErr(null)
    try {
      const att = await imageUpload.handleUpload(file)
      const id = timeline.addBeat()
      if (id)
        void timeline.regenerateBeatBackground(id, { shotKind: 'i2v', imageAttachmentId: att.id })
      onClose()
    } catch {
      setErr('Could not upload that image.')
    }
  }
  const onVideoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setErr(null)
    try {
      const att = await uploadVideo(file)
      const id = timeline.addBeat()
      if (id) {
        timeline.setBeatBackgroundById(id, {
          kind: 'video',
          clipSrc: { kind: 'attachment', attachmentId: att.id },
          shotKind: 't2v',
        })
      }
      onClose()
    } catch {
      /* validationError is surfaced below */
    }
  }

  return (
    <>
      {/* Click-away backdrop (menu floats over the dock). */}
      <div className="absolute inset-0 z-30" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-label="Add a beat"
        className="absolute top-12 right-4 z-40 w-[300px] rounded-xl border border-line bg-bg p-3 shadow-2xl"
        onKeyDown={e => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <span className="text-[10px] font-medium tracking-wide text-paper-faint uppercase">
          Add a beat
        </span>
        <div className="mt-2 flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 rounded-lg border border-line bg-bg-2 p-1">
            <input
              autoFocus
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  generate()
                }
              }}
              placeholder="Describe a scene to generate…"
              aria-label="Scene to generate for the new beat"
              className="h-7 min-w-0 flex-1 bg-transparent px-1.5 text-[12px] text-paper outline-none placeholder:text-paper-faint"
            />
            <button
              type="button"
              onClick={generate}
              disabled={busy || !prompt.trim()}
              className="flex h-7 items-center gap-1 rounded-md bg-acid px-2.5 text-[11px] font-semibold text-bg transition-opacity disabled:opacity-50"
            >
              <Sparkles size={12} /> Generate
            </button>
          </div>
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            disabled={busy}
            className="flex h-9 items-center gap-2 rounded-lg border border-line px-2.5 text-[12px] font-medium text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper disabled:opacity-60"
          >
            {imageUpload.isUploading ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <ImagePlus size={13} />
            )}
            Animate an image
          </button>
          <button
            type="button"
            onClick={() => {
              resetValidationError()
              videoInputRef.current?.click()
            }}
            disabled={busy}
            className="flex h-9 items-center gap-2 rounded-lg border border-line px-2.5 text-[12px] font-medium text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper disabled:opacity-60"
          >
            {uploadingVideo ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            Upload your video
          </button>
        </div>
        {(validationError || err) && (
          <span className="mt-1.5 block text-[10px] text-red-400" role="alert">
            {validationError?.message ?? err}
          </span>
        )}
        <input
          ref={videoInputRef}
          type="file"
          accept="video/mp4"
          className="hidden"
          onChange={onVideoFile}
        />
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onImageFile}
        />
      </div>
    </>
  )
}

// ───────────────────────── Toolbar (transport + render ONLY) ─────────────────────────

/** Human labels for the worker's coarse render stages (relayed live via videoRenderPhase).
 *  `broll` covers VO synth AND music prep — for a VO-less reel "Recording voiceover"
 *  claimed work that wasn't happening (F-13), so the label stays audio-neutral. */
export const RENDER_PHASE_LABEL: Record<string, string> = {
  decide: 'Planning',
  revise: 'Rewriting',
  broll: 'Preparing audio',
  render: 'Compositing video',
  judge: 'Scoring',
  finalize: 'Finishing up',
}

/** Whether any beat is still a blueprint-planned scene (no clip yet). */
export function hasPlannedBeats(timeline: VideoTimelineContextValue): boolean {
  return timeline.beats.some(
    b => b.background.kind === 'video' && b.background.planned === true && !b.background.clipSrc
  )
}

function TimelineToolbar({
  timeline,
  total,
  inspectorOpen,
  onToggleInspector,
}: {
  timeline: VideoTimelineContextValue
  total: number
  inspectorOpen: boolean
  onToggleInspector: () => void
}) {
  const draft = hasPlannedBeats(timeline)
  const hydrating = timeline.hydration != null
  return (
    <div className="relative flex items-center gap-3 border-b border-line/60 px-4 py-2">
      <button
        type="button"
        onClick={onToggleInspector}
        aria-label={inspectorOpen ? 'Hide the inspector' : 'Show the inspector'}
        aria-pressed={inspectorOpen}
        title={inspectorOpen ? 'Hide the inspector' : 'Show the inspector'}
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-lg transition-colors',
          inspectorOpen ? 'bg-bg-3 text-paper' : 'text-paper-dim hover:bg-bg-3 hover:text-paper'
        )}
      >
        <PanelLeft size={14} />
      </button>
      <span className="text-[12px] font-semibold text-paper">Timeline</span>
      <span className="text-[11px] text-paper-faint">{timeline.beats.length} beats</span>

      {/* Transport — absolutely centered (both axes) so the side clusters never push it around. */}
      <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2">
        <button
          type="button"
          onClick={() => (timeline.playing ? timeline.pause() : timeline.play())}
          aria-label={timeline.playing ? 'Pause' : 'Play'}
          title={timeline.playing ? 'Pause (space)' : 'Play (space)'}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-bg-3 text-paper transition-colors hover:bg-bg-4"
        >
          {timeline.playing ? <Pause size={16} /> : <Play size={16} className="translate-x-px" />}
        </button>
        <ReelTimecode
          timeline={timeline}
          total={total}
          className="text-[11px] text-paper-faint tabular-nums"
        />
      </div>

      <div className="ml-auto flex items-center gap-2.5">
        {timeline.renderError && (
          <span className="text-[11px] text-red-400" role="alert">
            {timeline.renderError}
          </span>
        )}
        {timeline.voicingBeatIds.size > 0 && (
          <span
            className="flex items-center gap-1 text-[10px] text-paper-dim"
            title="Synthesizing the voiceover so the preview speaks. Each voice drops in as it's ready."
          >
            <Loader2 size={11} className="animate-spin" /> Recording voice…
          </span>
        )}
        {timeline.editedSinceRender && !timeline.rendering ? (
          <span
            className="flex items-center gap-1 text-[10px] text-amber-400/90"
            title="Your edits aren't in the score or the video yet. Render to update."
          >
            <Sparkles size={11} /> Score reflects the last render · edits pending
          </span>
        ) : (
          <span className="hidden text-[10px] text-paper-faint lg:inline">
            Edits save live · Render rebuilds the video
          </span>
        )}
        {hydrating ? (
          // Blueprint hydration in flight: beats are being filmed server-side.
          <span className="flex h-8 items-center gap-1.5 rounded-full border border-acid/50 bg-acid/10 px-4 text-[12px] font-semibold text-paper">
            <Loader2 size={13} className="animate-spin" />
            Bringing to life · {timeline.hydration!.done}/{timeline.hydration!.total} — Filming
            scenes…
          </span>
        ) : draft ? (
          // A parked draft (insufficient credits / hydration failed): resume is one tap.
          <button
            type="button"
            onClick={() => void timeline.bringToLife()}
            className="flex h-8 items-center gap-1.5 rounded-full bg-acid px-4 text-[12px] font-semibold text-bg transition-opacity hover:opacity-90"
          >
            <Sparkles size={13} />
            Bring it to life
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void timeline.render()}
            disabled={timeline.rendering}
            className={cn(
              'flex h-8 items-center gap-1.5 rounded-full bg-acid px-4 text-[12px] font-semibold text-bg transition-opacity',
              timeline.rendering ? 'cursor-not-allowed opacity-60' : 'hover:opacity-90'
            )}
          >
            {timeline.rendering ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Sparkles size={13} />
            )}
            {timeline.rendering
              ? `Rendering${timeline.renderPhase && RENDER_PHASE_LABEL[timeline.renderPhase] ? `: ${RENDER_PHASE_LABEL[timeline.renderPhase]}` : ''}…`
              : 'Render preview'}
          </button>
        )}
      </div>
    </div>
  )
}

// ───────────────────────── The contextual inspector (one context at a time) ─────────────────────────

function ContextBar({
  timeline,
  focus,
  onAskAIBeat,
  selectedClipId,
  onClipGone,
}: {
  timeline: VideoTimelineContextValue
  focus: LaneFocus
  onAskAIBeat?: (index: number) => void
  selectedClipId: string | null
  onClipGone: () => void
}) {
  const index = timeline.activeIndex
  const beat = timeline.beats[index]
  if (!beat) return null
  return (
    <div>
      {focus === 'clip' && (
        <ClipContext timeline={timeline} index={index} beat={beat} onAskAIBeat={onAskAIBeat} />
      )}
      {focus === 'vo' && <VoContext key={beat.id} timeline={timeline} index={index} beat={beat} />}
      {focus === 'cc' && <CaptionsContext timeline={timeline} />}
      {focus === 'music' && <MusicContext timeline={timeline} />}
      {focus === 'layer' && (
        <LayerContext timeline={timeline} selectedClipId={selectedClipId} onClipGone={onClipGone} />
      )}
    </div>
  )
}

/** Clip context: everything you do to the selected BEAT.
 *  Reads top→bottom as identity → properties → content → AI → lifecycle:
 *    header · Timing (duration stepper + transition segmented) · Scene (the generate/replace
 *    verbs as full-width rows) · Ask AI · meta footer (reorder / duplicate / delete). */
export function ClipContext({
  timeline,
  index,
  beat,
  onAskAIBeat,
}: {
  timeline: VideoTimelineContextValue
  index: number
  beat: VideoBeatComposition
  onAskAIBeat?: (index: number) => void
}) {
  const [armed, setArmed] = useState(false)
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (armTimer.current) clearTimeout(armTimer.current)
    },
    []
  )
  useEffect(() => {
    setArmed(false)
  }, [index])
  const onDelete = () => {
    if (!armed) {
      setArmed(true)
      if (armTimer.current) clearTimeout(armTimer.current)
      armTimer.current = setTimeout(() => setArmed(false), 3000)
      return
    }
    if (armTimer.current) clearTimeout(armTimer.current)
    setArmed(false)
    timeline.deleteBeat(index)
  }
  const bg = beat.background
  const generating = timeline.generatingBeatIds.has(beat.id)
  const bgStatus = generating
    ? 'Filming a new clip…'
    : bg.kind === 'video' && bg.planned === true && !bg.clipSrc
      ? 'Planned scene · not filmed yet'
      : BG_LABEL[bg.kind]
  return (
    <div className="flex flex-col items-stretch gap-3.5">
      <PanelHeader
        kicker={`Beat ${index + 1} of ${timeline.beats.length}`}
        title={ROLE_LABEL[beat.role]}
        subtitle={`${beat.durationSec.toFixed(1)}s · ${bgStatus}`}
      />

      <Section label="Duration">
        <Stepper
          value={`${beat.durationSec.toFixed(1)}s`}
          decLabel="Shorten beat"
          incLabel="Lengthen beat"
          decDisabled={beat.durationSec <= MIN_BEAT_SECONDS}
          incDisabled={beat.durationSec >= MAX_BEAT_SECONDS}
          onDec={() => timeline.trimBeat(index, beat.durationSec - TRIM_STEP)}
          onInc={() => timeline.trimBeat(index, beat.durationSec + TRIM_STEP)}
        />
      </Section>

      {index > 0 && (
        <Section label="Enters with">
          <Segmented
            value={beat.transitionIn ?? 'cut'}
            onChange={t => timeline.setBeatTransition(index, t)}
            options={[
              { value: 'cut', label: 'Cut', hint: 'Hard cut from the previous beat' },
              { value: 'crossfade', label: 'Crossfade', hint: 'Dissolve ~0.4s across the cut' },
            ]}
          />
        </Section>
      )}

      {/* key by beat id so the scene-prompt field + transient upload state reset when the
          selection changes (the prompt prefills from THIS beat's sceneIdea). */}
      <BackgroundControl key={beat.id} timeline={timeline} index={index} beat={beat} />

      {onAskAIBeat && (
        <RowBtn
          label="Ask Taka about this beat"
          hint="Open the chat with this beat in focus"
          icon={<Sparkles size={13} />}
          onClick={() => onAskAIBeat(index)}
        />
      )}

      <MetaFooter>
        <MetaBtn
          label="Move beat earlier"
          disabled={index === 0}
          onClick={() => timeline.moveBeat(index, index - 1)}
        >
          <ArrowLeft size={13} />
        </MetaBtn>
        <MetaBtn
          label="Move beat later"
          disabled={index >= timeline.beats.length - 1}
          onClick={() => timeline.moveBeat(index, index + 1)}
        >
          <ArrowRight size={13} />
        </MetaBtn>
        <span className="flex-1" />
        {timeline.canAdd && (
          <MetaBtn label="Duplicate beat" onClick={() => timeline.duplicateBeat(index)}>
            <Copy size={13} />
          </MetaBtn>
        )}
        {timeline.canDelete && (
          <MetaBtn
            label={armed ? 'Tap again to delete' : 'Delete beat'}
            onClick={onDelete}
            danger={armed}
          >
            <Trash2 size={13} />
            {armed && <span>Confirm</span>}
          </MetaBtn>
        )}
      </MetaFooter>
    </div>
  )
}

/** Voiceover context: the script editor for the selected beat's VO (lives IN the VO object now). */
export function VoContext({
  timeline,
  index,
  beat,
  autoFocusScript = true,
}: {
  timeline: VideoTimelineContextValue
  index: number
  beat: VideoBeatComposition
  /** Mobile sheets pass false — auto-focusing the script yanks the keyboard open mid
   *  sheet-animation on iOS. Desktop keeps the focus (tapping the VO lane means "edit it"). */
  autoFocusScript?: boolean
}) {
  return (
    <div className="flex flex-col items-stretch gap-3.5">
      <PanelHeader
        kicker="Voiceover"
        icon={<Mic size={13} />}
        title={`Beat ${index + 1} · ${ROLE_LABEL[beat.role]}`}
        subtitle={
          !timeline.voEnabled
            ? 'Voiceover is off for this reel'
            : beat.voSrc && !beat.voDirty
              ? 'Voice recorded · plays in the preview'
              : 'Record to hear it in the preview'
        }
      />

      <Section label="Voiceover">
        <Segmented
          value={timeline.voEnabled ? 'on' : 'off'}
          onChange={v => timeline.setVoEnabled(v === 'on')}
          options={[
            { value: 'on', label: 'On' },
            {
              value: 'off',
              label: 'Off',
              hint: 'The reel plays without narration (captions drop too)',
            },
          ]}
        />
      </Section>

      {!timeline.voEnabled && (
        <span className="text-[10px] text-paper-faint">
          Voiceover is off, so the reel plays without narration. Scripts are kept; switch back on
          anytime.
        </span>
      )}

      {timeline.voEnabled && (
        <Section label="Voice">
          <select
            value={timeline.voVoiceId ?? REEL_VOICES[0]!.id}
            onChange={e => timeline.setVoice(e.target.value)}
            aria-label="Reel voice"
            className="h-8 w-full appearance-none rounded-lg border border-line bg-bg px-2.5 text-[12px] text-paper outline-none focus:border-acid"
          >
            {REEL_VOICES.map(v => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
          <span className="text-[10px] text-paper-faint">
            One voice for the whole reel · switching re-records each beat
          </span>
        </Section>
      )}

      {timeline.voEnabled && (
        <Section label="Script">
          <textarea
            defaultValue={beat.vo}
            key={`${beat.id}:${beat.vo}`}
            autoFocus={autoFocusScript}
            onBlur={e => {
              if (e.target.value !== beat.vo) timeline.setBeatVO(index, e.target.value)
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                ;(e.target as HTMLTextAreaElement).blur()
              }
            }}
            placeholder="What's spoken over this beat…"
            aria-label={`Voiceover for beat ${index + 1}`}
            className="block h-9 min-h-9 w-full resize-y rounded-lg border border-line bg-bg px-2.5 py-2 text-[12px] leading-snug text-paper outline-none placeholder:text-paper-faint focus:border-acid"
          />
          <span className="text-[10px] text-paper-faint">
            Captions re-time from the script automatically
          </span>
        </Section>
      )}

      {timeline.voEnabled && beat.vo.trim() && (!beat.voSrc || beat.voDirty) && (
        <RowBtn
          accent
          label={timeline.voicingBeatIds.has(beat.id) ? 'Recording…' : 'Record voice'}
          hint="Synthesize this beat's voice so the live preview speaks"
          icon={<Mic size={13} />}
          busy={timeline.voicingBeatIds.has(beat.id)}
          disabled={timeline.voicingBeatIds.has(beat.id)}
          onClick={() => timeline.voiceBeat(beat.id)}
        />
      )}
      {timeline.voEnabled && beat.vo.trim() && (
        <MetaFooter>
          <span className="flex-1" />
          <MetaBtn
            label="Clear this beat's voiceover"
            onClick={() => timeline.setBeatVO(index, '')}
          >
            <Trash2 size={13} />
            <span>Clear voiceover</span>
          </MetaBtn>
        </MetaFooter>
      )}
    </div>
  )
}

/** Curated ElevenLabs voices for the reel narration (one voice per reel). The first entry is
 *  the engine default (Rachel — must stay in sync with the worker's DEFAULT_VOICE). */
const REEL_VOICES: Array<{ id: string; label: string }> = [
  { id: '21m00Tcm4TlvDq8ikWAM', label: 'Rachel · warm, natural (default)' },
  { id: 'EXAVITQu4vr4xnSDxMaL', label: 'Bella · soft, friendly' },
  { id: 'AZnzlk1XvdvUeBnXmlld', label: 'Domi · confident, upbeat' },
  { id: 'pNInz6obpgDQGcFmaJgB', label: 'Adam · deep, assured' },
  { id: 'TxGEqnHWrfWFTfGW9XjX', label: 'Josh · young, energetic' },
  { id: 'ErXwobaYiN019PkySvjV', label: 'Antoni · smooth, warm' },
]

/** Captions context: on/off + placement + style — all deck-level (the whole reel). */
export function CaptionsContext({ timeline }: { timeline: VideoTimelineContextValue }) {
  return (
    <div className="flex flex-col items-stretch gap-3.5">
      <PanelHeader
        kicker="Captions"
        icon={<Type size={13} />}
        title="Word-timed from the voiceover"
        subtitle="Applies to the whole reel"
      />

      <Section label="Captions">
        <Segmented
          value={timeline.captionsEnabled ? 'on' : 'off'}
          onChange={v => timeline.setCaptionsEnabled(v === 'on')}
          options={[
            { value: 'on', label: 'On' },
            {
              value: 'off',
              label: 'Off',
              hint: 'No word-timed captions in the preview or the video',
            },
          ]}
        />
      </Section>

      {timeline.captionsEnabled ? (
        <>
          <Section label="Style">
            <Segmented
              value={timeline.captionStyle}
              onChange={s => timeline.setCaptionStyle(s)}
              options={[
                {
                  value: 'pop',
                  label: 'Pop',
                  hint: 'Bold caps with a halo, the classic Reels look',
                },
                { value: 'clean', label: 'Clean', hint: 'Smaller, minimal, mixed case' },
                { value: 'block', label: 'Block', hint: 'Your brand color as a highlight box' },
              ]}
            />
          </Section>

          <Section label="Placement">
            <Segmented
              value={timeline.captionPlacement}
              onChange={p => timeline.setCaptionPlacement(p)}
              options={[
                { value: 'bottom', label: 'Bottom' },
                { value: 'center', label: 'Center' },
                { value: 'top', label: 'Top' },
              ]}
            />
          </Section>

          <span className="text-[10px] text-paper-faint">
            Timings re-derive from the voiceover on Render
          </span>
        </>
      ) : (
        <span className="text-[10px] text-paper-faint">
          Captions are off. The reel shows no word-timed text.
        </span>
      )}
    </div>
  )
}

/** Music context: the bed as an object — on/off, a steered regenerate, or your own track. */
export function MusicContext({ timeline }: { timeline: VideoTimelineContextValue }) {
  const audioInputRef = useRef<HTMLInputElement>(null)
  const audioUpload = useFileUpload({ allowedTypes: ['audio/'], maxSize: 20 * 1024 * 1024 })
  const [promptOpen, setPromptOpen] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [uploadErr, setUploadErr] = useState<string | null>(null)
  const busy = timeline.musicGenerating || audioUpload.isUploading
  const onAudioFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploadErr(null)
    try {
      const att = await audioUpload.handleUpload(file)
      timeline.setMusicTrack({ kind: 'attachment', attachmentId: att.id })
    } catch {
      setUploadErr('Could not upload that audio file.')
    }
  }
  const onGenerate = () => {
    setPromptOpen(false)
    void timeline.regenerateMusic(prompt.trim() || undefined)
  }
  return (
    <div className="flex flex-col items-stretch gap-3.5">
      <PanelHeader
        kicker="Music"
        icon={<Music size={13} />}
        title={timeline.musicGenerating ? 'Composing a new bed…' : 'Mood-matched bed'}
        subtitle="Applies to the whole reel"
      />

      <Section label="Bed">
        <Segmented
          value={timeline.musicEnabled ? 'on' : 'off'}
          onChange={() => timeline.toggleMusic()}
          options={[
            { value: 'on', label: 'On' },
            { value: 'off', label: 'Off' },
          ]}
        />
      </Section>

      {timeline.musicEnabled && (
        <Section label="Track">
          <RowBtn
            label="Generate different music"
            hint="Describe a mood and compose a fresh instrumental bed"
            icon={<RefreshCw size={13} />}
            onClick={() => setPromptOpen(o => !o)}
            disabled={busy}
          />
          {promptOpen && (
            <div className="flex flex-col gap-1.5">
              <textarea
                value={prompt}
                autoFocus
                onChange={e => setPrompt(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    onGenerate()
                  }
                }}
                placeholder="Describe the vibe (e.g. slow jazzy piano)… empty = mood-matched"
                aria-label="Music prompt"
                className="block min-h-[52px] w-full resize-y rounded-lg border border-line bg-bg px-2.5 py-2 text-[12px] leading-snug text-paper outline-none placeholder:text-paper-faint focus:border-acid"
              />
              <button
                type="button"
                onClick={onGenerate}
                disabled={busy}
                className="flex h-8 w-full items-center justify-center gap-1.5 rounded-lg bg-acid text-[12px] font-semibold text-bg transition-opacity disabled:opacity-50"
              >
                <Sparkles size={13} /> Compose track
              </button>
            </div>
          )}
          <RowBtn
            label="Upload your own audio"
            hint="Use your own mp3/m4a/wav as the bed"
            icon={<Upload size={13} />}
            busy={audioUpload.isUploading}
            onClick={() => audioInputRef.current?.click()}
            disabled={busy}
          />
          <input
            ref={audioInputRef}
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={onAudioFile}
          />
          {timeline.musicGenerating && (
            <span className="flex items-center gap-1 text-[10px] text-acid" role="status">
              <Loader2 size={11} className="animate-spin" /> Composing… (~2-3 min, keeps working if
              you leave)
            </span>
          )}
          {uploadErr && (
            <span className="text-[10px] text-red-400" role="alert">
              {uploadErr}
            </span>
          )}
        </Section>
      )}

      <span className="text-[10px] text-paper-faint">
        {timeline.musicEnabled
          ? 'Mixed under the voiceover on Render'
          : 'Music is off. The reel renders without a bed.'}
      </span>
    </div>
  )
}

/** Layer-clip context: WHEN the selected clip shows on the reel (absolute effective seconds)
 *  + HOW it enters/exits + stack/duplicate/delete. Falls back to the canvas selection when no
 *  timeline clip is explicitly selected. No selection → a picker hint. */
export function LayerContext({
  timeline,
  selectedClipId,
  onClipGone,
}: {
  timeline: VideoTimelineContextValue
  selectedClipId: string | null
  onClipGone: () => void
}) {
  const composer = useComposerEditor()
  const clip =
    timeline.clips.find(c => c.layer.id === selectedClipId) ??
    timeline.clips.find(c => c.layer.id === composer?.editor.selectedId)
  if (!clip) {
    return (
      <div className="flex items-center gap-2 text-[12px] text-paper-faint">
        <Type size={13} />
        Tap a clip on the tracks (or any layer on the canvas) to time, animate and stack it.
      </div>
    )
  }
  const layer = clip.layer
  const total = timeline.totalDuration
  const setWin = (start: number, end: number) => timeline.setClipWindow(layer.id, start, end)
  const setAnim = (dir: 'in' | 'out', preset: TextAnimPreset | null) =>
    timeline.setClipAnim(layer.id, dir, preset)
  const animOptions = [
    { value: 'none', label: 'Cut' },
    { value: 'fade', label: 'Fade' },
    { value: 'slide', label: 'Slide' },
    { value: 'pop', label: 'Pop' },
  ]
  const step = 0.2
  return (
    <div className="flex flex-col items-stretch gap-3.5">
      <PanelHeader
        kicker={layer.type === 'text' ? 'Text clip' : 'Layer clip'}
        icon={layer.type === 'text' ? <Type size={13} /> : <ImageIcon size={13} />}
        title={layer.type === 'text' ? `“${textOf(layer) || 'Text'}”` : layerLabel(layer)}
        subtitle={`On screen ${clip.start.toFixed(1)}s – ${clip.end.toFixed(1)}s`}
      />

      <Section label="On screen">
        <div className="grid grid-cols-2 gap-1.5">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] text-paper-faint">From</span>
            <Stepper
              value={`${clip.start.toFixed(1)}s`}
              decLabel="Show earlier"
              incLabel="Show later"
              decDisabled={clip.start <= 0}
              incDisabled={clip.start >= clip.end - MIN_CLIP_SEC}
              onDec={() => setWin(clip.start - step, clip.end)}
              onInc={() => setWin(clip.start + step, clip.end)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] text-paper-faint">Until</span>
            <Stepper
              value={`${clip.end.toFixed(1)}s`}
              decLabel="Leave earlier"
              incLabel="Leave later"
              decDisabled={clip.end <= clip.start + MIN_CLIP_SEC}
              incDisabled={clip.end >= total}
              onDec={() => setWin(clip.start, clip.end - step)}
              onInc={() => setWin(clip.start, clip.end + step)}
            />
          </div>
        </div>
        <RowBtn label="Show for the whole reel" onClick={() => setWin(0, total)} />
      </Section>

      <Section label="Enters">
        <Segmented
          value={layer.anim?.in ?? 'none'}
          onChange={v => setAnim('in', v === 'none' ? null : (v as TextAnimPreset))}
          options={animOptions}
        />
      </Section>
      <Section label="Exits">
        <Segmented
          value={layer.anim?.out ?? 'none'}
          onChange={v => setAnim('out', v === 'none' ? null : (v as TextAnimPreset))}
          options={animOptions}
        />
      </Section>

      <MetaFooter>
        <MetaBtn
          label="Bring forward (stack up)"
          onClick={() => timeline.moveClipZ(layer.id, 'up')}
        >
          <ArrowUp size={13} />
        </MetaBtn>
        <MetaBtn
          label="Send backward (stack down)"
          onClick={() => timeline.moveClipZ(layer.id, 'down')}
        >
          <ArrowDown size={13} />
        </MetaBtn>
        <span className="flex-1" />
        <MetaBtn
          label="Duplicate this clip (Cmd+D)"
          onClick={() => timeline.duplicateClip(layer.id)}
        >
          <Copy size={13} />
        </MetaBtn>
        <MetaBtn
          label="Delete this clip (Delete)"
          onClick={() => {
            timeline.removeClip(layer.id)
            onClipGone()
          }}
        >
          <Trash2 size={13} />
        </MetaBtn>
      </MetaFooter>
    </div>
  )
}

/** Background section of the clip context — every way to set a beat's (text-free) background:
 *   • Upload video (K3): lock a user mp4 as the clip (deterministic; render skips motion-QC).
 *   • Regenerate (K4): edit a scene prompt → t2v generates a fresh clip.
 *   • Animate image (K5): upload a still → i2v animates it into a clip.
 *  Generation is async (1-2 min): the beat shows a "Generating…" state until the clip lands. */
function BackgroundControl({
  timeline,
  index,
  beat,
}: {
  timeline: VideoTimelineContextValue
  index: number
  beat: VideoBeatComposition
}) {
  const videoInputRef = useRef<HTMLInputElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const {
    handleUpload: uploadVideo,
    isUploading: uploadingVideo,
    validationError,
    resetValidationError,
  } = useVideoUpload({ fieldId: 'video', uploadMethod: 'button' })
  const imageUpload = useFileUpload({ allowedTypes: ['image/'], maxSize: 15 * 1024 * 1024 })
  const bg = beat.background
  const initialPrompt = bg.kind === 'card' ? '' : (bg.sceneIdea ?? '')
  const [prompt, setPrompt] = useState(initialPrompt)
  const [promptOpen, setPromptOpen] = useState(false)
  const [uploadErr, setUploadErr] = useState<string | null>(null)
  const generating = timeline.generatingBeatIds.has(beat.id)
  const busy = generating || uploadingVideo || imageUpload.isUploading

  const onVideoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      const att = await uploadVideo(file)
      timeline.setBeatBackground(index, {
        kind: 'video',
        clipSrc: { kind: 'attachment', attachmentId: att.id },
        shotKind: 't2v',
      })
    } catch {
      /* validationError is surfaced below */
    }
  }
  const onImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploadErr(null)
    try {
      const att = await imageUpload.handleUpload(file)
      await timeline.regenerateBeatBackground(beat.id, {
        shotKind: 'i2v',
        imageAttachmentId: att.id,
      })
    } catch {
      setUploadErr('Could not upload that image.')
    }
  }
  const onGenerate = () => {
    if (!prompt.trim()) return
    setPromptOpen(false)
    void timeline.regenerateBeatBackground(beat.id, { shotKind: 't2v', sceneIdea: prompt.trim() })
  }

  const planned = bg.kind === 'video' && bg.planned === true && !bg.clipSrc
  return (
    <Section label="Scene">
      <RowBtn
        accent={planned}
        label={planned ? 'Film this scene' : 'Regenerate scene'}
        hint="Edit the scene prompt and generate a new clip"
        icon={<RefreshCw size={13} />}
        onClick={() => setPromptOpen(o => !o)}
        disabled={busy}
      />
      {promptOpen && (
        <div className="flex flex-col gap-1.5">
          <textarea
            value={prompt}
            autoFocus
            onChange={e => setPrompt(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                onGenerate()
              }
            }}
            placeholder="Describe the scene to generate…"
            aria-label={`Scene prompt for beat ${index + 1}`}
            className="block min-h-[52px] w-full resize-y rounded-lg border border-line bg-bg px-2.5 py-2 text-[12px] leading-snug text-paper outline-none placeholder:text-paper-faint focus:border-acid"
          />
          <button
            type="button"
            onClick={onGenerate}
            disabled={busy || !prompt.trim()}
            className="flex h-8 w-full items-center justify-center gap-1.5 rounded-lg bg-acid text-[12px] font-semibold text-bg transition-opacity disabled:opacity-50"
          >
            <Sparkles size={13} /> Generate clip
          </button>
        </div>
      )}
      <RowBtn
        label="Animate a photo"
        hint="Upload a still image, we animate it into a clip"
        icon={<ImagePlus size={13} />}
        busy={imageUpload.isUploading}
        onClick={() => imageInputRef.current?.click()}
        disabled={busy}
      />
      <RowBtn
        label="Upload your own video"
        hint="Use your own vertical mp4 for this beat"
        icon={<Upload size={13} />}
        busy={uploadingVideo}
        onClick={() => {
          resetValidationError()
          videoInputRef.current?.click()
        }}
        disabled={busy}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/mp4"
        className="hidden"
        onChange={onVideoFile}
      />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onImageFile}
      />
      {generating && (
        <span className="flex items-center gap-1 text-[10px] text-acid" role="status">
          <Loader2 size={11} className="animate-spin" /> Generating a new clip… (~1 min)
        </span>
      )}
      {validationError && (
        <span className="text-[10px] text-red-400" role="alert">
          {validationError.message}
        </span>
      )}
      {uploadErr && (
        <span className="text-[10px] text-red-400" role="alert">
          {uploadErr}
        </span>
      )}
    </Section>
  )
}

function IconStep({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-5 w-5 items-center justify-center rounded text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  )
}

// ───────────────────────── Inspector vocabulary ─────────────────────────
// One shape per job so the panel reads as an interface, not a button dump:
//   PanelHeader — what's selected;  Section — labeled block;  Segmented — exclusive choice;
//   Stepper — numeric nudge;  RowBtn — full-width verb;  MetaFooter/MetaBtn — lifecycle row.

/** Panel header: kicker (what KIND of object) + name + a one-line live status. */
function PanelHeader({
  kicker,
  title,
  subtitle,
  icon,
}: {
  kicker: string
  title: string
  subtitle?: string
  icon?: ReactNode
}) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="text-[10px] font-medium tracking-wide text-paper-faint uppercase">
        {kicker}
      </span>
      <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-semibold text-paper">
        {icon}
        <span className="truncate">{title}</span>
      </span>
      {subtitle && <span className="mt-0.5 text-[10px] text-paper-faint">{subtitle}</span>}
    </div>
  )
}

/** A labeled block — every property group uses the same kicker + rhythm. */
function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] font-medium tracking-wide text-paper-faint uppercase">
        {label}
      </span>
      {children}
    </div>
  )
}

/** Exclusive choice as a full-width segmented control (options share the row equally). */
function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string; hint?: string }>
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="flex h-8 items-center gap-0.5 rounded-lg border border-line bg-bg p-0.5">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          title={o.hint}
          onClick={() => {
            if (o.value !== value) onChange(o.value)
          }}
          aria-pressed={o.value === value}
          className={cn(
            'h-full min-w-0 flex-1 truncate rounded-md px-1.5 text-[11px] font-medium transition-colors',
            o.value === value ? 'bg-acid text-bg' : 'text-paper-dim hover:text-paper'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** Numeric nudge: minus | value | plus spread across the full row. */
function Stepper({
  value,
  onDec,
  onInc,
  decDisabled,
  incDisabled,
  decLabel,
  incLabel,
}: {
  value: string
  onDec: () => void
  onInc: () => void
  decDisabled?: boolean
  incDisabled?: boolean
  decLabel: string
  incLabel: string
}) {
  return (
    <div className="flex h-8 items-center justify-between rounded-lg border border-line bg-bg px-1.5">
      <IconStep label={decLabel} disabled={decDisabled} onClick={onDec}>
        <Minus size={12} />
      </IconStep>
      <span className="text-[12px] font-semibold text-paper tabular-nums">{value}</span>
      <IconStep label={incLabel} disabled={incDisabled} onClick={onInc}>
        <Plus size={12} />
      </IconStep>
    </div>
  )
}

/** Full-width verb row — actions read as a scannable menu. `accent` marks the ONE
 *  suggested next action of a context (acid tint, never more than one per panel). */
function RowBtn({
  label,
  hint,
  onClick,
  disabled,
  busy,
  icon,
  accent,
}: {
  label: string
  hint?: string
  onClick: () => void
  disabled?: boolean
  busy?: boolean
  icon?: ReactNode
  accent?: boolean
}) {
  return (
    <button
      type="button"
      title={hint ?? label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex h-9 w-full items-center gap-2 rounded-lg border px-2.5 text-[12px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        accent
          ? 'border-acid/40 bg-acid/10 text-paper hover:bg-acid/15'
          : 'border-line text-paper-dim hover:bg-bg-3 hover:text-paper'
      )}
    >
      {busy ? <Loader2 size={13} className="animate-spin" /> : icon}
      <span className="truncate">{label}</span>
    </button>
  )
}

/** Lifecycle footer: reorder / duplicate / delete behind a divider, so destructive and
 *  housekeeping actions never mix with content controls. */
function MetaFooter({ children }: { children: ReactNode }) {
  return (
    <div className="mt-0.5 flex items-center gap-1 border-t border-line/60 pt-2.5">{children}</div>
  )
}

function MetaBtn({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  danger?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex h-8 min-w-8 items-center justify-center gap-1 rounded-lg px-1.5 text-[11px] font-medium transition-colors',
        danger ? 'bg-red-500 text-white' : 'text-paper-dim hover:bg-bg-3 hover:text-paper',
        disabled && 'cursor-not-allowed opacity-40'
      )}
    >
      {children}
    </button>
  )
}
